import { LeftOutlined } from '@teko/ds-icon';
import {
  Body,
  Button,
  Card,
  Flex,
  LazyImage,
  SubTitle,
  Switch,
  Title,
  styled,
} from '@teko/ds-mobile';
import TerraKit from '@terra-js/terra-kit';
import DOMPurify from 'dompurify';
import { isServerless } from 'helpers';
import { useCookiesConsent, useTenantConfig } from 'hooks';
import { t } from 'i18n';
import { useState } from 'react';
import CookieIcon from '../assets/cookie-outlined.svg';

export const CookiesConsentModal: React.FC = () => {
  const { isMobile, epicOmni1936, disableEpicOmni1936InWebview } =
    useTenantConfig();
  const {
    visible,
    isSubmitting,
    firstViewDescriptions,
    toggleablePolicies,
    onToggle,
    onAccept,
  } = useCookiesConsent();
  const [showList, setShowList] = useState(false);

  const renderContent = () => {
    return !showList ? (
      <>
        <Flex align="center" gap="s8" marginBottom="s12">
          <LazyImage
            alt="cookie-icon"
            width={23}
            height={22}
            src={CookieIcon}
          />
          <Title level={2} color="#101828">
            {t('Cookie & trải nghiệm website')}
          </Title>
        </Flex>

        {!!firstViewDescriptions[0] && (
          <Body
            level={1}
            dangerouslySetInnerHTML={{
              __html: DOMPurify?.sanitize?.(
                firstViewDescriptions[0].description,
              ),
            }}
          />
        )}

        <Flex gap="s8" marginTop="s16" align="center" justify="space-between">
          <Button
            size="m"
            block
            disabled={isSubmitting}
            onClick={() => setShowList(true)}
            padding="s4"
          >
            <SubTitle
              level={2}
              color="textSecondary"
              style={{ lineHeight: '16px' }}
            >
              {t('Danh sách loại cookie')}
            </SubTitle>
          </Button>
          <Button
            variant="primary"
            size="m"
            block
            disabled={isSubmitting}
            onClick={() => onAccept(true)}
          >
            <SubTitle level={2}>{t('Chấp nhận')}</SubTitle>
          </Button>
        </Flex>
      </>
    ) : (
      <>
        <Flex align="center" gap="s8" marginBottom="s12">
          <LeftOutlined
            onClick={() => setShowList(false)}
            size={20}
            color="#4A5565"
            style={{ cursor: 'pointer' }}
          />
          <Title level={2} color="#101828">
            {t('Danh sách loại cookie')}
          </Title>
        </Flex>

        <Body level={1} color="#4A5565">
          {t(
            'Chúng tôi sử dụng danh sách các loại cookie dưới đây để duy trì hoạt động website và nâng cao trải nghiệm mua sắm.',
          )}
          <br />
          {t(
            'Việc thay đổi một số lựa chọn dưới đây có thể ảnh hưởng đến trải nghiệm và sử dụng một số tính năng của website.',
          )}
        </Body>

        <Flex direction="column" marginTop="s16">
          {toggleablePolicies.map((policy) => (
            <Flex
              direction="row"
              width="100%"
              justify="space-between"
              align="center"
              padding={['s4', 's0']}
              key={policy.id}
            >
              <Body
                level={1}
                paddingRight="s4"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify?.sanitize?.(policy.description),
                }}
              />
              <Flex direction="column" justify="center" align="end">
                <Switch
                  disabled={isSubmitting}
                  checked={policy.isApproved ?? false}
                  onChange={() => onToggle(policy.id)}
                />
              </Flex>
            </Flex>
          ))}
        </Flex>

        <Flex gap="s8" marginTop="s16" align="center" justify="space-between">
          <Button
            size="m"
            block
            disabled={isSubmitting}
            onClick={() => onAccept()}
          >
            <SubTitle level={2} color="textSecondary">
              {t('Xác nhận')}
            </SubTitle>
          </Button>
          <Button
            variant="primary"
            size="m"
            block
            padding="s4"
            disabled={isSubmitting}
            onClick={() => onAccept(true)}
          >
            <SubTitle level={2}>{t('Chấp nhận tất cả')}</SubTitle>
          </Button>
        </Flex>
      </>
    );
  };

  if (
    !isServerless() || // render component client side only
    !epicOmni1936 ||
    (!!disableEpicOmni1936InWebview && !!TerraKit.isAvailable()) || // hide component when web is opened in webview
    !visible ||
    !toggleablePolicies.length
  )
    return null;

  return !!isMobile ? (
    <MobileOverlay>
      <MobileCard padding="s16">{renderContent()}</MobileCard>
    </MobileOverlay>
  ) : (
    <DesktopOverlay>
      <DesktopCard
        padding="s24"
        margin={['s24', 's16']}
        borderRadius="xl"
        backgroundColor="white"
        border="1px solid #F3F4F6"
        maxWidth={480}
      >
        {renderContent()}
      </DesktopCard>
    </DesktopOverlay>
  );
};

const DesktopOverlay = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const MobileOverlay = styled.div`
  position: fixed;
  bottom: 0;
  z-index: 9999;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const DesktopCard = styled(Card)`
  box-shadow: 0px 5px 12px 4px #00000017, 0px 3px 6px 0px #0000001f,
    0px 1px 2px -2px #00000029;
`;

const MobileCard = styled(Card)`
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  box-shadow: 0px -2px 4px 0px #e8e8e8;
`;
